import React, { ReactElement, useState, useRef, useLayoutEffect, useEffect, useMemo } from 'react';
import classNames from 'classnames/bind';
import { connect } from 'react-redux';
import Image from '../../../refresh/atoms/ImageView';
import styles from './index.pcss';
import Text from '../../../refresh/atoms/Text';
import { setAdditionalInfo } from '../../../actions/shared/productsData';
import {
  getPriceRevealSaleDuration,
  getEarlyAccessDetails,
  getEalyAccessText
} from './priceRevealUtil';
import { setPrimaryHeight } from '../../../actions/webMetaData';
import properties from '../../../modules/ajio-util/properties';
import { DURING_SALE, PRE_BUZZ } from './messages';
import { signInCheck } from '../CheckUserStatus';

interface PriceRevealProps {
  productsAdditionalInfo: {
    isPriceReveal: boolean;
    priceRevealMetaInfo: {
      startTime: string;
      endTime: string;
    };
  };
  deviceType: string;
  setAdditionalInfo: (p: { isPriceReveal: boolean }) => void;
  setPrimaryHeight: (p: number) => void;
  flags: {
    priceReveal: string;
  };
  store: string;
  sis: boolean;
  luxe: boolean;
}

interface ReduxStateReturnProps {
  productsAdditionalInfo: {
    isPriceReveal: boolean;
    priceRevealMetaInfo: {
      startTime: string;
      endTime: string;
    };
  };
  flags: {
    priceReveal: string;
  };
  store: string;
  sis: boolean;
}

interface ReduxStateProps {
  productsAdditionalInfo: {
    isPriceReveal: boolean;
    priceRevealMetaInfo: {
      startTime: string;
      endTime: string;
    };
  };
  flags: {
    priceReveal: string;
  };
  metaData: {
    name: string;
    sis: boolean;
  };
}

const mapStateToProps = (state: ReduxStateProps): ReduxStateReturnProps => ({
  productsAdditionalInfo: state.productsAdditionalInfo,
  flags: state.flags,
  store: state.metaData.name || 'AJIO',
  sis: state.metaData.sis
});

const mapDispatchToProps = { setAdditionalInfo, setPrimaryHeight };

const connector = connect(mapStateToProps, mapDispatchToProps);

const cx = classNames.bind(styles);

const EarlyAccessBanner = (props: PriceRevealProps): ReactElement | null => {
  const {
    productsAdditionalInfo: {
      isPriceReveal = false,
      priceRevealMetaInfo = {},
      dealStartTime = '',
      dealEndTime = ''
    } = {},
    setAdditionalInfo,
    setPrimaryHeight,
    deviceType = 'msite',
    flags = {},
    store = 'AJIO',
    sis = false,
    luxe
  } = props;
  if (dealStartTime !== '' || dealEndTime !== '') return null;
  const earlyAccessConfigData = useMemo<{}>(() => {
    return getEarlyAccessDetails(flags) as {
      earlyAccessText: string;
      BBSText: string;
      earlyAccessSaleStartText: string;
      bannerImgSrc: any;
      priceImgSrc: any;
    };
  }, [flags]);

  const priceRevealState = getPriceRevealSaleDuration(
    flags,
    deviceType,
    priceRevealMetaInfo,
    store
  );

  const [isSticky, setSticky] = useState(false);
  const ref = useRef(null);

  const scrollCallback = (): void => {
    if (ref && ref.current) {
      setSticky(ref.current.getBoundingClientRect().bottom <= 49);
    }
  };
  const throttled = useRef(scrollCallback);

  useLayoutEffect(() => {
    window.addEventListener('scroll', throttled.current);

    return () => {
      window.removeEventListener('scroll', () => throttled.current);
    };
  }, []);

  useEffect(() => {
    const username = signInCheck();
    let defaultPriceReveal = false;
    if (priceRevealState === DURING_SALE) {
      defaultPriceReveal = true;
    }
    if (priceRevealState === PRE_BUZZ) {
      if (username && localStorage.getItem('priceReveal_v2')) {
        defaultPriceReveal = true;
      }
    }
    setAdditionalInfo({
      isPriceReveal: defaultPriceReveal
    });
    if (priceRevealState) {
      setPrimaryHeight(140);
    }
  }, []);

  if (!priceRevealState) {
    return <div />;
  }

  if (deviceType === 'desktop') {
    return (
      <div
        className={cx(
          luxe ? 'luxeEarlyAccessPriceRevealDesktop' : 'earlyAccessPriceRevealDesktop',
          { storeLayout: sis }
        )}
      >
        <div>
          <Image src={earlyAccessConfigData?.bannerImgSrc?.image} alt={earlyAccessConfigData?.bannerImgSrc?.altText || "price-reveal"} />
        </div>
        <div className={cx('boldText', 'paddingBottom', 'colorDarkBlue')} aria-label={earlyAccessConfigData?.earlyAccessText}>
          {earlyAccessConfigData?.earlyAccessText}
        </div>
        <span className={cx('vertical-line')} />
        <div className={cx('paddingBottom', 'colorBlack')} aria-label={`${earlyAccessConfigData?.BBSText} ${getEalyAccessText(priceRevealMetaInfo)}`}>
          {earlyAccessConfigData?.BBSText} {getEalyAccessText(priceRevealMetaInfo)}
        </div>
      </div>
    );
  }
  return (
    <div className={cx(luxe ? 'luxePriceRevealBanner' : 'earlyAccessPLPBanner')} ref={ref}>
      {!isSticky && (
        <div className={cx('expandBanner')}>
          <div className={cx('earlyAccessLogoContainer')}>
            <Image src={earlyAccessConfigData?.bannerImgSrc?.image} alt={earlyAccessConfigData?.bannerImgSrc?.altText ||"price-reveal"} />
          </div>
          <div className={cx('earlyAccessTextContainer')}>
            <Text variant="T3" color={luxe ? 'Light_Black' : 'Dark_Blue_Grey'} weight="bold">
              {earlyAccessConfigData?.earlyAccessText}
            </Text>
            <br />
            <Text variant="T2" color="Nero">
              {earlyAccessConfigData?.BBSText} {getEalyAccessText(priceRevealMetaInfo)}
            </Text>
            <br />
            <Text variant="T2" customClass={cx('colorGray')}>
              {earlyAccessConfigData?.earlyAccessSaleStartText}
            </Text>
          </div>
        </div>
      )}
      {isSticky && (
        <div className={cx('sticky', luxe ? 'luxeCollapsedBanner' : 'collapsedBanner')}>
          <div className={cx('earlyAccessLogoContainer', 'text-center')}>
            <Image src={earlyAccessConfigData?.priceImgSrc?.image} alt={earlyAccessConfigData?.priceImgSrc?.altText || "price-reveal" } />
          </div>
          <div className={cx('collapsedTextContainer')}>
            <Text variant="T3" color="Dark_Blue_Grey" weight="bold">
              {earlyAccessConfigData?.earlyAccessText}
            </Text>
          </div>
        </div>
      )}
    </div>
  );
};

export default connector(EarlyAccessBanner);
